/**
 * Settings → Alerts & Reports → Unbilled deployments.
 *
 * Read-only reconciliation: every unit sitting on a customer through an OPEN
 * deployment while nothing is billing behind it (no linked rental and no open
 * rental_committed hold). Internal staff + superadmin only — the server is the
 * enforcement point; the sidebar entry is gated on `unbilled_deployments_view`.
 *
 * Design: semantic tokens only, tabular-nums on numeric/date columns, coral
 * (#E0556C) reserved for genuinely CONTRADICTORY data (a status that disagrees
 * with the linkage, a group owned by another company) and nothing else. No new
 * visual language.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReceiptText, Loader2, Download, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  getUnbilledDeployments,
  downloadUnbilledDeploymentsCsv,
  UNBILLED_FLAG_BADGES,
  type UnbilledDeploymentsResponse,
  type UnbilledDeployment,
  type UnbilledFlag,
} from '@/lib/unbilledDeploymentsApi';

/** Flags that mean the data CONTRADICTS itself — the only ones that get coral. */
const CONTRADICTION_FLAGS = new Set<UnbilledFlag>([
  'status_rented_without_rental',
  'group_company_mismatch',
]);

/** `no_rental_link` is true of every row, so showing it as a badge is noise. */
function visibleFlags(row: UnbilledDeployment): UnbilledFlag[] {
  return row.flags.filter((f) => f !== 'no_rental_link');
}

function daysSince(iso: string): number | null {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

export function UnbilledDeployments() {
  const [data, setData] = useState<UnbilledDeploymentsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await getUnbilledDeployments());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadUnbilledDeploymentsCsv();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const items = useMemo(() => data?.items ?? [], [data]);

  return (
    <div className="p-4" data-testid="unbilled-deployments">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <ReceiptText className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#8FB560]" />
          <div>
            <h1 className="text-lg font-semibold text-foreground">Unbilled deployments</h1>
            <p className="max-w-prose text-xs text-muted-foreground">
              Units deployed to a customer with nothing billing behind them — no linked rental
              and no open rental hold. Read-only; nothing here changes a booking.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); load(); }}
            disabled={loading} aria-label="Refresh report">
            <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          </Button>
          <Button variant="outline" size="sm" onClick={onExport}
            disabled={exporting || items.length === 0} className="gap-2">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </Button>
        </div>
      </header>

      {loading && (
        <Card><CardContent className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </CardContent></Card>
      )}

      {!loading && error && (
        <Card><CardContent className="py-8">
          <div className="flex items-start gap-2 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#E0556C]" />
            <div>
              <p className="font-medium text-foreground">Couldn’t load the report</p>
              <p className="text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" className="mt-3"
                onClick={() => { setLoading(true); load(); }}>Try again</Button>
            </div>
          </div>
        </CardContent></Card>
      )}

      {!loading && !error && items.length === 0 && (
        <Card><CardContent className="py-10 text-center">
          <p className="text-sm font-medium text-foreground">Nothing unbilled</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Every unit deployed to a customer has a rental or an open hold behind it.
          </p>
        </CardContent></Card>
      )}

      {!loading && !error && items.length > 0 && (
        <>
          <p className="mb-3 text-sm text-muted-foreground">
            <span className="font-semibold tabular-nums text-[#E0556C]">{items.length}</span>
            {items.length === 1 ? ' unit is' : ' units are'} deployed to a customer with nothing
            billing behind {items.length === 1 ? 'it' : 'them'}.
          </p>

          <Card>
            <CardContent className="p-0">
              {/* Table wraps itself in overflow-auto; min-w makes it scroll
                  rather than squish on a phone. */}
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Unit</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Production / Site</TableHead>
                    <TableHead className="text-right">Deployed</TableHead>
                    <TableHead>Deployed by</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Why flagged</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((row) => {
                    const days = daysSince(row.deployed_since);
                    return (
                      <TableRow key={row.deployment_id} data-testid={`row-${row.unit_code}`}>
                        <TableCell className="font-medium tabular-nums">{row.unit_code}</TableCell>
                        <TableCell>{row.company_name ?? '—'}</TableCell>
                        <TableCell>
                          <span>{row.production_name ?? '—'}</span>
                          {row.site_name && (
                            <span className="block text-xs text-muted-foreground">{row.site_name}</span>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right tabular-nums">
                          <span>{formatDate(row.deployed_since)}</span>
                          {days != null && (
                            <span className="block text-xs text-muted-foreground">
                              {days} {days === 1 ? 'day' : 'days'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          <span>{row.deployed_by_name ?? '—'}</span>
                          {row.deployment_reason && (
                            <span className="block text-xs">{row.deployment_reason}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{row.current_status ?? '—'}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {visibleFlags(row).length === 0 && (
                              <span className="text-xs text-muted-foreground">No rental linked</span>
                            )}
                            {visibleFlags(row).map((f) => (
                              <Badge
                                key={f}
                                variant="outline"
                                className={
                                  CONTRADICTION_FLAGS.has(f)
                                    ? 'border-[#E0556C]/40 text-[#E0556C]'
                                    : 'text-muted-foreground'
                                }
                              >
                                {UNBILLED_FLAG_BADGES[f]}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {data?.generated_at && (
            <p className="mt-3 text-xs text-muted-foreground tabular-nums">
              Generated {new Date(data.generated_at).toLocaleString()}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default UnbilledDeployments;
